import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import GameCard from './GameCard';
import GameCardLive from './GameCardLive';
import type { Game } from '../hooks/useGames';
import type { GameLive } from '../hooks/useGamesLive';

interface GameRowProps {
    title: string;
    description?: string;
    games: Game[];
    size?: 'small' | 'medium' | 'large';
    onGamePlay?: (game: Game) => void | Promise<void>;
    onGameInfo?: (game: Game) => void;
    onGamePromoClick?: (promoUrl: string) => void;
    categoryType?: string; // Per identificare le categorie di tipo "rank"
    gamesLive?: GameLive[]; // Array dei giochi live per trovare corrispondenze
}

const GameRow = ({ title, description, games, size = 'medium', onGamePlay, onGameInfo, onGamePromoClick, categoryType, gamesLive }: GameRowProps) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const rowRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
        const container = rowRef.current;
        if (container) {
            const scrollAmount = size === 'large' ? 400 : size === 'small' ? 200 : 300;
            const newPosition = direction === 'left'
                ? Math.max(0, scrollPosition - scrollAmount)
                : scrollPosition + scrollAmount;

            container.scrollTo({ left: newPosition, behavior: 'smooth' });
            setScrollPosition(newPosition);
        }
    };

    const handleScroll = () => {
        if (rowRef.current) {
            setScrollPosition(rowRef.current.scrollLeft);
        }
    };

    if (!games || games.length === 0) {
        return (
            <div className="mb-8 py-3">
                {/* Titolo categoria su due righe */}
                <div className="mb-4 px-6">
                    <h2 className="text-white text-3xl font-medium leading-tight">{title}</h2>
                    {description && (
                        <p className="text-gray-400 text-sm mt-1 leading-tight">{description}</p>
                    )}
                </div>
                {/* Non mostriamo "Nessun gioco disponibile" per mantenere coerenza grafica */}
            </div>
        );
    }

    return (
        <div className="mb-8 py-3">
            {/* Titolo categoria su due righe */}
            <div className="mb-4 px-6">
                <h2 className="text-white text-3xl font-medium leading-tight">{title}</h2>
                {description && (
                    <p className="text-gray-400 text-sm mt-1 leading-tight">{description}</p>
                )}
            </div>

            <div className="relative group">
                {/* Freccia sinistra */}
                <button
                    onClick={() => scroll('left')}
                    className={`absolute left-6 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70 ${
                        scrollPosition <= 0 ? 'hidden' : ''
                    }`}
                >
                    <ChevronLeft size={20} />
                </button>

                {/* Freccia destra */}
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70"
                >
                    <ChevronRight size={20} />
                </button>

                {/* Container dei giochi */}
                <div
                    ref={rowRef}
                    className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2 pl-6 pr-6"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    onScroll={handleScroll}
                >
                    {games.map((game, index) => {
                        // Se è un gioco live e abbiamo i dati live, usa GameCardLive
                        if (game.type === 'casinolive' && gamesLive) {
                            const liveGame = gamesLive.find(lg => lg.id === game.id);
                            if (liveGame) {
                                return (
                                    <GameCardLive
                                        key={game.id}
                                        gameLive={liveGame}
                                        game={game}
                                        onPlay={onGamePlay}
                                    />
                                );
                            }
                        }
                        
                        // Altrimenti usa GameCard normale
                        return (
                            <GameCard
                                key={game.id}
                                game={game}
                                size={size}
                                onPlay={onGamePlay}
                                onInfo={onGameInfo}
                                onPromoClick={onGamePromoClick}
                                rank={categoryType === 'RANK' ? index + 1 : undefined}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default GameRow;